'use client'

import { useForm } from 'react-hook-form'
import { Card, Container, Form, Heading, Icon, LoadingSkeleton, RangeSlider, TextField, Tooltip } from '@boltenergy-be/design-system'
import { EvDiscountFormInputs } from './types'
import styles from './EvDiscountForm.module.scss'
import { useTranslation } from 'next-i18next/pages'
import {
  AVERAGE_EV_CHARGING_USAGE,
  DEFAULT_DISTANCE,
  MAX_DISTANCE,
  MIN_DISTANCE,
  EV_FLEX_ADVANTAGE,
  EV_FLEX_SIMULATION_PRICE_REQUEST
} from './EvDiscountForm.constants'
import parse from 'html-react-parser'
import { useCallback, useEffect } from 'react'
import { formatAmount } from 'utils/format'
import { useGetLanguage } from 'hooks/useGetLanguage'
import debounce from 'debounce'
import Link from 'components/link/Link'
import { SIMULATE_ID } from 'sections/start-simulation/StartSimulation.constants'
import { useLazySimulatePriceQuery } from 'store/api/bolt-api'

const EvDiscountForm = () => {
  // Dynamic route params
  const { t } = useTranslation(['electricVehicles', 'validation', 'common'])
  const language = useGetLanguage()

  // React Hook Form
  const { register, setValue, watch, formState } = useForm<EvDiscountFormInputs>({
    defaultValues: {
      distance: DEFAULT_DISTANCE
    }
  })
  const watchDistance = watch('distance')

  // Redux queries
  const [simulatePrice, { data, isFetching }] = useLazySimulatePriceQuery()

  // Constants
  const avgChargingCost = data ? data.price.electricity.total * 12 : 0

  /**
   * Submits the form with the distance input and calculates the average charging cost.
   * @param {number} distance
   */
  const simulateChargingCost = async ({ distance }: EvDiscountFormInputs) => {
    const averageChargingHours = (distance / 100) * AVERAGE_EV_CHARGING_USAGE // Average EV usage is 16 kWh per 100 km

    await simulatePrice({
      ...EV_FLEX_SIMULATION_PRICE_REQUEST,
      meterUsage: {
        electricity: {
          consumption: {
            single: averageChargingHours
          }
        }
      }
    })
  }

  const debouncedSimulate = useCallback(debounce(simulateChargingCost, 300), [])

  // Initialize the discount calculation with the default distance
  useEffect(() => {
    simulateChargingCost({ distance: DEFAULT_DISTANCE })
  }, [])

  return (
    <Container as="section" classnames={{ content: styles.root }}>
      <div className={styles['form-wrapper']}>
        <Heading as="h2" variant="h2" serif>
          {parse(t('calculator.title'))}
        </Heading>
        <p>{t('calculator.description')}</p>

        <Form className={styles.form}>
          <RangeSlider
            label={t('calculator.label')}
            name="kilometerSlider"
            min={MIN_DISTANCE}
            max={MAX_DISTANCE}
            step={1000}
            value={watchDistance}
            onChange={(km) => {
              setValue('distance', km, { shouldValidate: true })
              debouncedSimulate({ distance: km })
            }}
          />

          <TextField
            {...register('distance')}
            disabled
            readOnly
            min={MIN_DISTANCE}
            max={MAX_DISTANCE}
            grid="full"
            label={t('calculator.label')}
            hiddenLabel
            type="number"
            value={watchDistance || MIN_DISTANCE}
            onChange={(e) => setValue('distance', e.target.valueAsNumber, { shouldValidate: true })}
            layoutClassName={styles.distance}
            error={formState?.errors?.distance?.message}
          />
        </Form>

        <Link representation="button" as="a" href={`#${SIMULATE_ID}`} trailingIcon="chevronRight">
          {t('howItWorks.action')}
        </Link>
      </div>

      <Card color="vibrant" className={styles.calculations}>
        <dl>
          <div>
            <dt>
              <Tooltip tooltipContent={<p>{t('expectedCharging')}</p>}>
                <Icon name="questionMarkCircle" />
              </Tooltip>
              <p>{t('calculator.table.estimatedCost')}</p>
            </dt>
            <dd>
              {isFetching ? (
                <LoadingSkeleton.Rectangle width={100} height={24} />
              ) : avgChargingCost ? (
                t('common:formattedValue.currency', { value: formatAmount(avgChargingCost, language) })
              ) : (
                '—'
              )}
            </dd>
          </div>

          <hr />

          <div>
            <dt>
              <Tooltip tooltipContent={<p>{t('firstYearBenefit')}</p>}>
                <Icon name="questionMarkCircle" />
              </Tooltip>
              <p>{t('calculator.table.advantageFlex')}</p>
            </dt>
            <dd>{t('common:formattedValue.currency', { value: EV_FLEX_ADVANTAGE })}</dd>
          </div>

          <hr className={styles.thick} />

          <div>
            <dt>{t('calculator.table.discount')}</dt>
            <dd>
              {isFetching ? (
                <LoadingSkeleton.Rectangle width={100} height={24} />
              ) : avgChargingCost ? (
                t('common:formattedValue.percentage', { value: formatAmount((EV_FLEX_ADVANTAGE / avgChargingCost) * 100, language) })
              ) : (
                '—'
              )}
            </dd>
          </div>
        </dl>
      </Card>
    </Container>
  )
}

export default EvDiscountForm
